// Presentation vocabulary for frameworks and mappings (APP-004 R3 UI-P3).
//
// The rules these badges encode are compliance-relevant: an AI suggestion must
// never read as approved or covered, protected text must be visibly protected,
// and a tenant-owned framework must be distinguishable from the global
// catalogue. Keeping them in one module stops a screen from inventing its own
// wording for a state the backend already named.
import { Badge } from "@odingard/design-system";
import type { BadgeTone } from "@odingard/design-system";
import type {
  CoverageStatus,
  FrameworkContentPolicy,
  FrameworkScope,
  FrameworkVersionStatus,
  MappingMethod,
  MappingState,
  RequirementContentKind,
} from "@odingard/sdk-client";

export function ScopeBadge({ scope }: { scope: FrameworkScope }) {
  return (
    <Badge tone={scope === "global" ? "info" : "neutral"}>
      {scope === "global" ? "Global catalogue" : "Tenant owned framework"}
    </Badge>
  );
}

const CONTENT_POLICY: Record<FrameworkContentPolicy, { label: string; tone: BadgeTone }> = {
  identifier_only: { label: "Identifier only content", tone: "warning" },
  summary_permitted: { label: "Odingard authored summary permitted", tone: "success" },
  licensed_only: { label: "Licensed content", tone: "danger" },
};

export function ContentPolicyBadge({ policy }: { policy: FrameworkContentPolicy }) {
  const it = CONTENT_POLICY[policy];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function contentPolicyNote(policy: FrameworkContentPolicy): string {
  switch (policy) {
    case "identifier_only":
      return "Odingard distributes identifiers and hierarchy only for this framework. Requirement text is not stored, and summaries cannot be added.";
    case "licensed_only":
      return "Standard text for this framework requires a live tenant licence that permits full text. Without one the backend withholds the content.";
    case "summary_permitted":
      return "Odingard authored summaries are permitted for this framework.";
  }
}

const CONTENT_KIND: Record<RequirementContentKind, { label: string; tone: BadgeTone }> = {
  licensed_text: { label: "Licensed content", tone: "danger" },
  odingard_summary: { label: "Odingard authored summary", tone: "info" },
  customer_note: { label: "Your note", tone: "neutral" },
};

export function ContentKindBadge({ kind }: { kind: RequirementContentKind }) {
  const it = CONTENT_KIND[kind];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const VERSION_STATUS: Record<FrameworkVersionStatus, BadgeTone> = {
  draft: "neutral",
  active: "success",
  superseded: "warning",
  retired: "danger",
};

export function VersionStatusBadge({ status }: { status: FrameworkVersionStatus }) {
  return <Badge tone={VERSION_STATUS[status]}>{status}</Badge>;
}

const COVERAGE: Record<CoverageStatus, { label: string; tone: BadgeTone }> = {
  covered: { label: "Covered", tone: "success" },
  weak: { label: "Weakly covered", tone: "warning" },
  stale: { label: "Stale", tone: "warning" },
  uncovered: { label: "Uncovered", tone: "danger" },
};

export function CoverageBadge({ status }: { status: CoverageStatus }) {
  const it = COVERAGE[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

/**
 * State and method are shown together because they mean different things: an
 * `ai_suggested` mapping that is still `proposed` is an unverified suggestion
 * and contributes nothing to coverage, while the same mapping once `verified`
 * is a human decision.
 */
export function MappingStateBadge({ state, method }: { state: MappingState; method: MappingMethod }) {
  if (state === "proposed") {
    return method === "ai_suggested" ? (
      <Badge tone="warning">Unverified AI suggestion</Badge>
    ) : (
      <Badge tone="neutral">Proposed, awaiting verification</Badge>
    );
  }
  if (state === "verified") return <Badge tone="success">Human verified mapping</Badge>;
  if (state === "rejected") return <Badge tone="danger">Rejected mapping</Badge>;
  if (state === "stale") return <Badge tone="warning">Stale mapping</Badge>;
  return <Badge tone="neutral">Superseded mapping</Badge>;
}

const METHOD: Record<MappingMethod, string> = {
  manual: "Manual",
  ai_suggested: "AI proposed",
  imported: "Imported",
  derived: "Derived",
};

export function methodLabel(method: MappingMethod): string {
  return METHOD[method];
}
